import { Dialog } from '@base-ui/react/dialog'
import { Link, Outlet, useRouterState } from '@tanstack/react-router'
import { useState } from 'react'
import { FilterIcon, XIcon } from '@/components/ui/icons'
import { IconButton } from '@/components/ui/primitives'
import type { MessageKey } from '@/i18n/types'
import { useT } from '@/i18n/use-t'
import { usesMockAPI } from '@/lib/api/backend-adapters'
import { cn } from '@/lib/cn'
import { useLocale } from '@/lib/queries'
import { MockBanner } from './app-shell'

type AdminTo =
  | '/$locale/admin'
  | '/$locale/admin/users'
  | '/$locale/admin/crawler'
  | '/$locale/admin/orders'
  | '/$locale/admin/config'
  | '/$locale/admin/content'

const GROUPS: { title: MessageKey; items: { key: MessageKey; to?: AdminTo }[] }[] = [
  { title: 'admin.groupOverview', items: [{ key: 'admin.overview', to: '/$locale/admin' }] },
  {
    title: 'admin.groupUsers',
    items: [
      { key: 'admin.users', to: '/$locale/admin/users' },
      { key: 'admin7.orders', to: '/$locale/admin/orders' },
    ],
  },
  { title: 'admin.groupContent', items: [{ key: 'admin7.content', to: '/$locale/admin/content' }] },
  { title: 'admin.groupCrawl', items: [{ key: 'admin7.tasks', to: '/$locale/admin/crawler' }] },
  { title: 'admin.groupSystem', items: [{ key: 'admin7.config', to: '/$locale/admin/config' }] },
]

function AdminNav({ onNavigate }: { onNavigate?: () => void }) {
  const locale = useLocale()
  const t = useT()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  return (
    <nav aria-label={t('brand.admin')} className="flex flex-col">
      {GROUPS.map((g) => (
        <div key={g.title} className="flex flex-col">
          <div className="px-3 pt-3.5 pb-1.5 text-[11px] font-bold tracking-[0.08em] text-dim">
            {t(g.title)}
          </div>
          {g.items
            .filter(
              (it) =>
                !usesMockAPI ||
                ['/$locale/admin', '/$locale/admin/users', '/$locale/admin/crawler'].includes(it.to ?? ''),
            )
            .map((it) => {
              if (!it.to)
                return (
                  <span
                    key={it.key}
                    className="flex h-[34px] items-center gap-2 rounded-lg px-3 text-sm text-dim"
                    title={t('admin.notInScope')}
                  >
                    <span className="h-3.5 w-[3px]" />
                    {t(it.key)}
                  </span>
                )
              const href = it.to.replace('$locale', locale)
              const on =
                it.to === '/$locale/admin'
                  ? pathname === href || pathname === `${href}/`
                  : pathname.startsWith(href)
              return (
                <Link
                  key={it.key}
                  to={it.to}
                  params={{ locale }}
                  onClick={onNavigate}
                  aria-current={on ? 'page' : undefined}
                  className={cn(
                    'flex h-[34px] items-center gap-2 rounded-lg px-3 text-sm',
                    on ? 'bg-dusk text-moon' : 'text-haze hover:text-moon',
                  )}
                >
                  <span
                    className={cn('h-3.5 w-[3px] rounded-sm', on ? 'bg-violet-text' : 'bg-transparent')}
                  />
                  {t(it.key)}
                </Link>
              )
            })}
        </div>
      ))}
    </nav>
  )
}

function Brand() {
  const t = useT()
  return (
    <div className="flex items-center gap-2.5 px-2 pb-3">
      <img src="/brand/icon-96.png" alt="" width={28} height={28} className="size-7 rounded-lg" />
      <span className="text-[15px] font-bold">{t('brand.admin')}</span>
      <span className="ml-auto rounded-[5px] bg-violet-bg px-1.5 py-0.5 font-mono text-[10px] text-violet-soft">
        {t('admin.roleAdmin')}
      </span>
    </div>
  )
}

export function AdminShell() {
  const locale = useLocale()
  const t = useT()
  const [open, setOpen] = useState(false)
  return (
    <div className="flex min-h-dvh flex-col">
      <MockBanner />
      <div className="flex flex-1">
        <aside className="sticky top-0 hidden h-dvh w-[232px] shrink-0 flex-col border-r border-line-soft bg-bar px-3 py-4.5 lg:flex">
          <Brand />
          <AdminNav />
          <Link
            to="/$locale/today"
            params={{ locale }}
            className="mt-auto flex h-9 items-center px-3 text-[13px] text-faint"
          >
            {t('admin.backToApp')}
          </Link>
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex h-14 items-center gap-2 border-b border-line-soft px-3 lg:hidden">
            <IconButton label={t('brand.admin')} onClick={() => setOpen(true)}>
              <FilterIcon size={20} />
            </IconButton>
            <span className="text-[15px] font-bold">{t('brand.admin')}</span>
          </div>
          <Outlet />
        </div>
      </div>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-50 bg-black/60" />
          <Dialog.Popup className="fixed inset-y-0 left-0 z-50 flex w-72 flex-col overflow-y-auto border-r border-line bg-bar px-3 py-4 outline-none transition-transform duration-200 data-[ending-style]:-translate-x-full data-[starting-style]:-translate-x-full">
            <Dialog.Title className="sr-only">{t('brand.admin')}</Dialog.Title>
            <div className="flex items-start">
              <Brand />
              <Dialog.Close
                aria-label={t('common.close')}
                className="ml-auto inline-flex size-9 items-center justify-center rounded-lg text-haze"
              >
                <XIcon size={18} />
              </Dialog.Close>
            </div>
            <AdminNav onNavigate={() => setOpen(false)} />
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}

export function AdminHeader({
  title,
  sub,
  actions,
}: {
  title: string
  sub?: string
  actions?: React.ReactNode
}) {
  return (
    <header className="flex min-h-[72px] flex-wrap items-center gap-3.5 border-b border-line-soft px-4 py-3 lg:px-8">
      <div className="flex flex-col gap-0.5">
        <h1 className="text-xl font-bold">{title}</h1>
        {sub ? <p className="text-xs text-faint">{sub}</p> : null}
      </div>
      {actions ? <div className="ml-auto flex flex-wrap items-center gap-2.5">{actions}</div> : null}
    </header>
  )
}
